// =============================================================================
// client/src/pages/CallSheet.jsx | v1.0 | SQ-S1 | The Call Sheet — /call-sheet
// =============================================================================
// Per-team-member to-do list of service tasks with dates. Reads
// GET /api/call-sheet/board?scope=mine|all.
//
// · admin / manager: Mine / All tabs (All default).
// · recruiting_coordinator: no tabs; the server forces mine.
// · anyone else: the server answers 403 and that sentence is rendered verbatim.
//   The Dashboard card and this page also hide behind can('call_sheet.view'),
//   so a role that would 403 is not offered the door in the first place.
//
// Pending tasks only (it is a to-do list — completed history lives on the
// profile tab). Overdue = deadline before the server's `today`: red row
// treatment and sorted to the top. NO date controls here; dates are edited on
// the recruit's profile. "Task Completed" opens CompleteTaskModal → POST
// complete → refetch.
//
// Chrome: PlanProgress.jsx's page + table pattern, formatDateUTC for the bare
// YYYY-MM-DD columns.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/api.js';
import { useMe } from '../lib/flux/useMe.js';
import CompleteTaskModal from '../components/CompleteTaskModal.jsx';
import Toast from '../components/Toast.jsx';
import NotificationBell from '../components/NotificationBell.jsx';
import {
  CALL_SHEET_STATUS_COLORS, displayStatus, formatDateUTC, seqLabel, recruitName,
  sortForBoard, errorMessage, todayLocalISO, taskTitle, taskParent,
} from '../lib/callSheet.js';

const SCOPES = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'Mine' },
];

export default function CallSheet() {
  const navigate = useNavigate();
  const { me, loading: meLoading, can, isAdminOrManager } = useMe();

  const [scope, setScope] = useState('all');
  const [tasks, setTasks] = useState([]);
  const [today, setToday] = useState(todayLocalISO());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (which) => {
    setLoading(true);
    try {
      const data = await api.get(`/api/call-sheet/board?scope=${encodeURIComponent(which)}`);
      setTasks(data.tasks || []);
      if (data.today) setToday(data.today);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the Call Sheet'));
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (meLoading) return;
    load(scope);
  }, [meLoading, scope, load]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  async function complete({ completed_on, notes }) {
    if (!completing) return;
    setBusy(true);
    try {
      await api.post(`/api/call-sheet/tasks/${completing.id}/complete`, { completed_on, notes });
      setCompleting(null);
      setToast({ tone: 'success', message: 'Task marked completed' });
      await load(scope);
    } catch (err) {
      setToast({ tone: 'error', message: errorMessage(err, 'Could not complete the task') });
    } finally {
      setBusy(false);
    }
  }

  const rows = useMemo(() => sortForBoard(tasks, today), [tasks, today]);
  const overdueCount = useMemo(() => rows.filter((t) => displayStatus(t, today) === 'overdue').length, [rows, today]);

  // Page-level gate: the server refuses regardless; this only avoids offering
  // a table that would 403. While /me is loading, render nothing decisive.
  const allowed = !meLoading && can('call_sheet.view');

  return (
    <div className="min-h-screen bg-silver-a">

      {/* Header — PlanProgress.jsx chrome */}
      <header className="bg-hunter-deep border-b border-hunter">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-display font-black text-white text-2xl leading-none">CCF Front Office</div>
            <div className="text-mid-green text-[0.65rem] tracking-[0.25em] uppercase font-bold mt-1">
              Coach Cole Football
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <NotificationBell />
            {me && (
              <span className="text-silver-a">
                Welcome, <span className="font-bold text-white">{me.team.full_name}</span>
              </span>
            )}
            <Link to="/" className="text-silver-a hover:text-white underline">← Front Office</Link>
            <button onClick={signOut} className="text-silver-a hover:text-white underline">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-black text-2xl text-silver-text">The Call Sheet</h1>
            <p className="text-sm text-silver-mute mt-1">
              Service tasks due for your recruits. Dates are set on each recruit's profile.
            </p>
          </div>
          {allowed && isAdminOrManager && (
            <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden" role="tablist" aria-label="Scope">
              {SCOPES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={scope === s.id}
                  onClick={() => setScope(s.id)}
                  className={`px-4 py-1.5 text-sm font-bold ${scope === s.id ? 'bg-hunter text-white' : 'text-hunter hover:bg-silver-a'}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {!meLoading && !allowed && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4 mb-4">
            The Call Sheet is not available for this role.
          </div>
        )}

        {allowed && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4 mb-4">
            {error}
          </div>
        )}

        {allowed && !error && (
          loading ? (
            <div className="text-sm text-silver-mute py-12 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-silver-mute py-12 text-center">Nothing due.</div>
          ) : (
            <>
              {overdueCount > 0 && (
                <p className="text-xs font-bold uppercase tracking-wide text-red-700 mb-3">
                  {overdueCount} overdue
                </p>
              )}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-[0.65rem] font-bold uppercase tracking-wide text-silver-mute">
                      <th className="px-4 py-3">Recruit</th>
                      <th className="px-4 py-3">Service</th>
                      <th className="px-4 py-3">Start</th>
                      <th className="px-4 py-3">Deadline</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => {
                      const status = displayStatus(t, today);
                      const overdue = status === 'overdue';
                      const seq = seqLabel(t);
                      return (
                        <tr
                          key={t.id}
                          className={`border-b border-gray-100 last:border-0 ${overdue ? 'bg-red-50/70' : ''}`}
                        >
                          <td className={`px-4 py-3 font-medium ${overdue ? 'text-red-800' : 'text-silver-text'}`}>
                            <Link to={`/recruits/${t.recruit_id}?tab=callsheet`} className="hover:underline">
                              {recruitName(t)}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-silver-text">
                            {taskTitle(t)}
                            {seq && <span className="ml-2 text-xs text-silver-mute">{seq}</span>}
                            {taskParent(t) && <div className="text-xs text-silver-mute">{taskParent(t)}</div>}
                          </td>
                          <td className="px-4 py-3 text-silver-text">{formatDateUTC(t.start_date)}</td>
                          <td className={`px-4 py-3 ${overdue ? 'text-red-800 font-bold' : 'text-silver-text'}`}>
                            {formatDateUTC(t.deadline_date)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[0.6rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${CALL_SHEET_STATUS_COLORS[status] || 'bg-gray-100 text-silver-text'}`}>
                              {status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              type="button"
                              className="btn-primary text-xs py-1 px-3"
                              disabled={busy}
                              onClick={() => setCompleting(t)}
                            >
                              Task Completed
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}
      </main>

      <CompleteTaskModal
        open={!!completing}
        task={completing}
        today={today}
        busy={busy}
        onConfirm={complete}
        onCancel={() => setCompleting(null)}
      />
      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}
